import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import { adsServer } from "../../server";
import { getStoredCurrentProfile } from '../../utils/shopProfile';

const SavePaymentMethod = ({ onPaymentMethodRetrieved = () => {} }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [openModal, setOpenModal] = useState(false); 
  const [setupClientSecret, setSetupClientSecret] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const requestHeaders = useMemo(() => {
    const token = localStorage.getItem('token');
    return {
      "Content-Type": "application/json",
      'current-profile': getStoredCurrentProfile() || 'defaultProfile',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${adsServer}/sponsored/payment-methods`, {
        withCredentials: true, 
        headers: requestHeaders,
      });

      const methods = response.data.paymentMethods || [];
      setPaymentMethods(methods);
      setErrorMessage('');
      onPaymentMethodRetrieved(methods.length > 0);
    } catch (error) {
      console.error('Error fetching payment method:', error.response?.data);
      setPaymentMethods([]);
      setErrorMessage(error.response?.data?.message || error.response?.data?.error || 'Payment methods could not be loaded.');
      onPaymentMethodRetrieved(false);
    }
  }, [onPaymentMethodRetrieved, requestHeaders]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const openAddPaymentMethodModal = async () => {
    setOpenModal(true);
    setSetupClientSecret('');

    try {
      const { data } = await axios.post(`${adsServer}/sponsored/payment-methods/setup-intent`, {}, {
        withCredentials: true,
        headers: requestHeaders,
      });
      setSetupClientSecret(data.clientSecret || '');
    } catch (error) {
      console.error('Error creating setup intent:', error.response?.data || error);
      setErrorMessage(error.response?.data?.message || error.response?.data?.error || 'Failed to start payment method setup.');
      setOpenModal(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !setupClientSecret) {
      return;
    }

    setLoading(true);
    const cardElement = elements.getElement(CardElement);
    const { error, setupIntent } = await stripe.confirmCardSetup(setupClientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (error) {
      console.error('[error]', error);
      setErrorMessage(error.message);
      setLoading(false);
    } else {
      try {
        const paymentMethodId = typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method?.id;

        if (!paymentMethodId) {
          throw new Error('Stripe did not return a saved payment method.');
        }

        const response = await axios.post(`${adsServer}/sponsored/save-payment-method`, {
          paymentMethodId,
        }, { withCredentials: true, headers: requestHeaders });

        alert('Payment method saved successfully!');
        setErrorMessage('');
        fetchPaymentMethods();
        setOpenModal(false);
      } catch (err) {
        console.error('Error saving payment method:', err.response?.data);
        setErrorMessage(err.response?.data?.message || err.response?.data?.error || 'Failed to save the payment method.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="save-payment-method-container">
      <div className="save-payment-method-header">
        <h2>Saved Payment Methods</h2>
        <p>Cards are stored securely with Stripe for subscriptions and ad purchases.</p>
      </div>
      {errorMessage && (
        <div className="save-payment-method-error" role="alert">
          {errorMessage}
        </div>
      )}
      {paymentMethods.length > 0 ? (
        <div className="save-payment-method-details">
          <h3>Payment Methods:</h3>
          {paymentMethods.map((paymentMethod) => (
            <div key={paymentMethod.id} className="save-payment-method-card-row">
              <p>Brand: {paymentMethod.brand}</p>
              <p>Last four: {paymentMethod.last4}</p>
              <p>Expiry: {paymentMethod.exp_month}/{paymentMethod.exp_year}</p>
              {paymentMethod.isDefault && <p>Default</p>}
            </div>
          ))}
          <button onClick={openAddPaymentMethodModal} className="save-payment-method-open-btn">
            Add New Payment Method
          </button>
        </div>
      ) : (
        <>
          <button onClick={openAddPaymentMethodModal} className="save-payment-method-open-btn">
            Add Payment Method
          </button>
        </>
      )}

      {openModal && (
        <div className="save-payment-method-modal">
          <div className="save-payment-method-modal-content">
            <span onClick={() => setOpenModal(false)} className="save-payment-method-close-btn">&times;</span>
            <h2>Add Payment Method</h2>
            <form onSubmit={handleSubmit} className="save-payment-method-form">
              <CardElement className="save-payment-method-card-element" />
              <button type="submit" className="save-payment-method-submit-btn" disabled={!stripe || !setupClientSecret || loading}>
                {loading ? "Saving..." : "Save Payment Method"}
              </button>
            </form>
            <p className="save-payment-method-disclaimer">
              By saving your payment method, you agree that this payment method will be stored securely in Stripe and will be kept on file for all subscription and ad-related purchases and activities.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavePaymentMethod;